/* eslint-disable @typescript-eslint/no-empty-object-type */
import { z } from 'zod';
import {
  ApplicantOfferRequirementStatus,
  ApplicantOfferResponseStatus,
  ApplicantOfferStatus,
  ApplicantStatusReason,
  ScholarshipType
} from './enums';
import { IApplicantOfferRequirement } from './requirements';

export const UpdateApplicantOfferStatusSchema = z
  .object({
    status: z.nativeEnum(ApplicantOfferStatus),
    conditions: z.array(z.string()).optional(),
    statusReason: z.nativeEnum(ApplicantStatusReason).optional(),
    statusInternalNote: z.string().optional(),
    notifyApplicant: z.boolean().optional()
  })
  .superRefine((data, ctx) => {
    const { status, conditions, statusReason } = data;

    const statusesRequiresConditions: ApplicantOfferStatus[] = [
      ApplicantOfferStatus.Conditional,
      ApplicantOfferStatus.Unconditional
    ];
    if (statusesRequiresConditions.includes(status) && conditions === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['conditions'],
        message: `Status ${status} requires conditions`
      });
    }
    if ([ApplicantOfferStatus.Withdrawn, ApplicantOfferStatus.Rejected].includes(status) && !statusReason) {
      ctx.addIssue({
        code: 'custom',
        path: ['statusReason'],
        message: `Status ${status} requires status reason`
      });
    }
    return true;
  });

export interface IUpdateApplicantOfferStatus extends z.infer<typeof UpdateApplicantOfferStatusSchema> {}

export const UpdateApplicantOfferLetterTextSchema = z
  .object({
    startText: z.string().optional(),
    endText: z.string().optional()
  })
  .superRefine((data, ctx) => {
    const { startText, endText } = data;

    if (!startText && !endText) {
      ctx.addIssue({
        code: 'custom',
        path: ['startText', 'endText'],
        message: `start text or end text is required`
      });
    }
    return true;
  });

export interface IUpdateApplicantOfferLetterText extends z.infer<typeof UpdateApplicantOfferLetterTextSchema> {}

export const UpdateApplicantOfferRequirementStatusSchema = z
  .object({
    status: z.nativeEnum(ApplicantOfferRequirementStatus),
    noteText: z.string().optional(),
    conditionText: z.string().optional()
  })
  .superRefine((data, ctx) => {
    const { status, noteText, conditionText } = data;

    if (status === ApplicantOfferRequirementStatus.Condition && !conditionText) {
      ctx.addIssue({
        code: 'custom',
        path: ['conditionText'],
        message: `ConditionText required when updating to status ${status}`
      });
    }
    const statusesRequiresNote: ApplicantOfferRequirementStatus[] = [
      ApplicantOfferRequirementStatus.Exempt,
      ApplicantOfferRequirementStatus.NotSatisfied
    ];
    if (statusesRequiresNote.includes(status) && !noteText) {
      ctx.addIssue({
        code: 'custom',
        path: ['noteText'],
        message: `Reason required when updating to status ${status}`
      });
    }
    return true;
  });

export function enhanceUpdateApplicantOfferRequirementStatusSchema(
  previousRequirement: IApplicantOfferRequirement,
  offerNeedsReissue: boolean
) {
  return UpdateApplicantOfferRequirementStatusSchema.superRefine((data, ctx) => {
    const updateCondition = data.status === ApplicantOfferRequirementStatus.Condition;
    // If the issue does not need reissuing, we don't need any note text
    if (offerNeedsReissue && updateCondition) {
      const previousWasCondition = previousRequirement.status === ApplicantOfferRequirementStatus.Condition;
      if (previousWasCondition && !data.noteText) {
        ctx.addIssue({
          code: 'custom',
          path: ['noteText'],
          message: 'Note text must be specified when resetting conditon'
        });
      }
    }
  });
}

export interface IUpdateApplicantOfferRequirementStatus
  extends z.infer<typeof UpdateApplicantOfferRequirementStatusSchema> {}

const FixedScholarshipSchema = z.object({
  name: z.string(),
  type: z.literal(ScholarshipType.FIXED),
  amount: z.number()
});

const PercentageScholarshipSchema = z.object({
  name: z.string(),
  type: z.literal(ScholarshipType.PERCENTAGE),
  percentage: z.number()
});

const ScholarshipSchema = z.discriminatedUnion('type', [FixedScholarshipSchema, PercentageScholarshipSchema]);

// TODO: This is duplicated from IApplicantOffer['programme'] which has types from IApplyCourse. How do we do this?
export const UpdateApplicantOfferProgrammeDetailsSchema = z.object({
  entryPoint: z.enum(['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6']),
  startDate: z.string().date(),
  endDate: z.string().date(),
  fee: z.number(),
  scholarships: z.array(ScholarshipSchema).optional()
});

export interface IUpdateApplicantOfferProgrammeDetails
  extends z.infer<typeof UpdateApplicantOfferProgrammeDetailsSchema> {}

export const StaffCreateApplicantOfferSchema = UpdateApplicantOfferProgrammeDetailsSchema.extend({
  intakeId: z.string(),
  courseId: z.string()
});

export interface IStaffCreateApplicantOffer extends z.infer<typeof StaffCreateApplicantOfferSchema> {}

export const UpdateApplicantOfferStatusActionSchema = z.object({
  applicantStatus: z.nativeEnum(ApplicantOfferResponseStatus)
});

export interface ApplicantOfferStatusQueryParams {
  offerId: string;
}

export interface IApplicantOfferStatusAction extends z.infer<typeof UpdateApplicantOfferStatusActionSchema> {}

export interface UpdateApplicantOfferStatusQueryParams extends ApplicantOfferStatusQueryParams {}
export const CreateApplicantOfferRequirementSchema = z.object({
  requirementId: z.string(),
  requirementText: z.string().optional(),
  conditionText: z.string().optional()
});

export const CreateApplicantOfferRequirementsSchema = z.object({
  requirements: z.array(CreateApplicantOfferRequirementSchema)
});

export interface ICreateApplicantOfferRequirements extends z.infer<typeof CreateApplicantOfferRequirementsSchema> {}

export const ReinstateApplicantOfferSchema = z.object({});

export interface IReinstateApplicantOffer extends z.infer<typeof ReinstateApplicantOfferSchema> {}
